<template>
    <Overlay :show="loadingCatalog">
        <b-card no-body>
            <div class="card-header manifest-editor__card-header">
                <div>
                    <b-card-title class="mb-25">Manifest configuration</b-card-title>
                    <small class="text-muted">
                        {{ draftSourceLabel }}
                        <span v-if="hasDraftChanges" class="text-warning"> · Unsaved changes</span>
                    </small>
                </div>
                <div class="manifest-editor__draft-controls">
                    <b-form-select
                        v-model="selectedSourceVersionId"
                        :options="sourceVersionOptions"
                        :disabled="disabled || loadingSourceVersion || !sourceVersionOptions.length"
                        size="sm"
                        aria-label="Configuration source revision"
                    />
                    <b-button
                        size="sm"
                        variant="outline-primary"
                        :disabled="!canLoadSelectedVersion"
                        @click="loadSelectedVersion"
                    >
                        <b-spinner v-if="loadingSourceVersion" small class="mr-50" />
                        {{ loadingSourceVersion ? 'Loading' : 'Load' }}
                    </b-button>
                    <b-button
                        size="sm"
                        variant="outline-danger"
                        :disabled="disabled || loadingSourceVersion || !canResetConfiguration"
                        @click="resetConfiguration"
                    >
                        Reset
                    </b-button>
                </div>
            </div>
            <b-alert v-if="sourceVersionError" class="m-2 mb-0" variant="danger" show>
                <div class="alert-body">{{ sourceVersionError }}</div>
            </b-alert>
            <b-alert v-if="catalogError" class="m-2 mb-0" variant="danger" show>
                <div class="alert-body">
                    The editor catalog could not be loaded. Reload the page before editing this manifest.
                </div>
            </b-alert>

            <div class="manifest-editor">
                <aside
                    class="manifest-editor__navigation"
                    :class="{'manifest-editor__navigation--collapsed': navigationCollapsed}"
                >
                    <div class="manifest-editor__navigation-header">
                        <span class="manifest-editor__navigation-title text-muted">
                            Steps
                        </span>
                        <b-button
                            size="sm"
                            variant="flat-secondary"
                            class="btn-icon manifest-editor__navigation-toggle"
                            :title="navigationCollapsed ? 'Expand steps' : 'Collapse steps'"
                            :aria-label="navigationCollapsed ? 'Expand steps' : 'Collapse steps'"
                            @click="navigationCollapsed = !navigationCollapsed"
                        >
                            <feather-icon
                                :icon="navigationCollapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'"
                                size="18"
                            />
                        </b-button>
                    </div>

                    <button
                        v-for="(step, index) in steps"
                        :key="step.id"
                        type="button"
                        class="manifest-editor__step"
                        :class="{
                            'manifest-editor__step--active': index === activeStepIndex,
                            'manifest-editor__step--completed': isStepCompleted(index),
                            'manifest-editor__step--disabled': !canOpenStep(index),
                        }"
                        :aria-disabled="!canOpenStep(index) ? 'true' : 'false'"
                        :tabindex="canOpenStep(index) ? 0 : -1"
                        @click="openStep(index)"
                    >
                        <span
                            v-b-tooltip.hover.right="navigationCollapsed ? stepTooltip(step) : ''"
                            class="manifest-editor__step-marker"
                        >
                            <feather-icon
                                v-if="isStepCompleted(index)"
                                icon="CheckIcon"
                                size="16"
                            />
                            <span v-else>{{ index + 1 }}</span>
                        </span>
                        <span class="manifest-editor__step-copy">
                            <span class="manifest-editor__step-title">{{ step.title }}</span>
                            <span class="manifest-editor__step-description">
                                {{ step.navigationDescription }}
                            </span>
                        </span>
                    </button>
                </aside>

                <section class="manifest-editor__workspace">
                    <header class="manifest-editor__workspace-header">
                        <div>
                            <div class="text-muted text-uppercase font-small-2 mb-25">
                                Step {{ activeStepIndex + 1 }} of {{ steps.length }}
                            </div>
                            <h4 class="mb-25">{{ activeStep.title }}</h4>
                            <p class="text-muted mb-0">{{ activeStep.description }}</p>
                        </div>
                        <b-badge v-if="catalog" variant="light-success">
                            Editor catalog loaded
                        </b-badge>
                    </header>

                    <div class="manifest-editor__workspace-body">
                        <ManifestSourceStep
                            v-if="activeStep.id === 'source'"
                            v-model="sourceState"
                            :disabled="disabled"
                            @error="handleBackendError"
                        />

                        <ManifestProductBehaviorStep
                            v-else-if="activeStep.id === 'product'"
                            v-model="productState"
                            :drivers="drivers"
                            :disabled="disabled"
                            @retry-requirements="resolveMappingRequirements"
                        />

                        <ManifestMappingStep
                            v-else-if="activeStep.id === 'mapping'"
                            v-model="mappingState"
                            :source-tab="selectedSourceTab"
                            :canonical-fields="canonicalFields"
                            :components="mappingComponents"
                            :record-components="recordPipelineComponents"
                            :requirements="productState.requirements"
                            :disabled="disabled"
                        />

                        <ManifestPlanningStep
                            v-else-if="activeStep.id === 'planning'"
                            v-model="planningState"
                            :policies="planningPolicies"
                            :disabled="disabled"
                        />

                        <ManifestReviewStep
                            v-else-if="activeStep.id === 'review'"
                            :definition="manifestDefinition"
                            :build-issues="definitionBuildIssues"
                            :version-result="createdVersion"
                            @edit-step="openStepById"
                        />

                        <ManifestEditorPlaceholderStep
                            v-else
                            :step="activeStep"
                            :source-summary="sourceSummary"
                        />
                    </div>

                    <footer class="manifest-editor__workspace-footer">
                        <b-button
                            variant="outline-secondary"
                            :disabled="activeStepIndex === 0"
                            @click="previousStep"
                        >
                            <feather-icon icon="ChevronLeftIcon" class="mr-50" />
                            Back
                        </b-button>

                        <b-button
                            v-if="!isLastStep"
                            variant="primary"
                            :disabled="!canContinue"
                            @click="nextStep"
                        >
                            Continue
                            <feather-icon icon="ChevronRightIcon" class="ml-50" />
                        </b-button>

                        <b-button
                            v-else
                            variant="primary"
                            :disabled="!canCreateVersion"
                            @click="createVersion"
                        >
                            <b-spinner v-if="creatingVersion" small class="mr-50" />
                            {{ createVersionButtonLabel }}
                        </b-button>
                    </footer>
                </section>
            </div>
        </b-card>
    </Overlay>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardTitle,
    BFormSelect,
    BSpinner,
    VBTooltip,
} from 'bootstrap-vue'
import {ImportManifestApi} from '@core/api'
import Overlay from '@core/components/common/Overlay.vue'
import backendResponse from '@core/mixins/ui/backend-response'
import ManifestEditorPlaceholderStep from './editor/ManifestEditorPlaceholderStep.vue'
import ManifestMappingStep from './editor/ManifestMappingStep.vue'
import ManifestPlanningStep from './editor/ManifestPlanningStep.vue'
import ManifestProductBehaviorStep from './editor/ManifestProductBehaviorStep.vue'
import ManifestReviewStep from './editor/ManifestReviewStep.vue'
import ManifestSourceStep from './editor/ManifestSourceStep.vue'
import {configurationIssues} from './editor/manifestConfigurationSchema'
import {buildManifestDefinition} from './editor/manifestDefinitionBuilder'
import {hydrateManifestDefinition} from './editor/manifestDefinitionHydrator'

const STEPS = [
    {
        id: 'source',
        title: 'Source',
        navigationDescription: 'Upload and inspect',
        description: 'Upload a representative source file and confirm how its spreadsheet is structured.',
    },
    {
        id: 'product',
        title: 'Import behavior',
        navigationDescription: 'Driver, structure and identity',
        description: 'Select the domain driver and configure the behavior that determines mapping requirements.',
    },
    {
        id: 'mapping',
        title: 'Field mapping',
        navigationDescription: 'Columns and pipelines',
        description: 'Map inspected source columns to canonical fields and satisfy driver requirements.',
    },
    {
        id: 'planning',
        title: 'Planning',
        navigationDescription: 'Execution policy',
        description: 'Select the policy that groups the domain plan into executable work.',
    },
    {
        id: 'review',
        title: 'Review',
        navigationDescription: 'Validate and create version',
        description: 'Review the generated definition, create an immutable revision, and inspect validation results.',
    },
]

const createSourceState = () => ({
    file: null,
    uploadedFile: null,
    inspection: null,
    selectedTabIndex: null,
    headerRow: null,
    dataStartRow: null,
    selector: null,
    sourceType: 'spreadsheet',
    acceptedFileTypes: ['xlsx', 'xls', 'csv'],
    sourceExtras: {},
    tabExtras: {},
    fromVersion: null,
})

const createMappingState = () => ({
    sourceKey: null,
    rows: [],
    selectedRowId: null,
    recordPipeline: [],
    tabExtras: {},
})

const createProductState = () => ({
    driver: null,
    driverVersion: null,
    strategy: null,
    identityPolicy: null,
    identityField: null,
    caseSensitive: true,
    existingPolicy: null,
    existingIdentityField: null,
    existingIdentityFields: [],
    configuration: null,
    requirements: [],
    requirementsLoaded: false,
    requirementsError: false,
    loadingRequirements: false,
})

const createPlanningState = () => ({
    policy: null,
    policyVersion: null,
    configuration: {},
})

export default {
    components: {
        BAlert,
        BBadge,
        BButton,
        BCard,
        BCardTitle,
        BFormSelect,
        BSpinner,
        ManifestEditorPlaceholderStep,
        ManifestMappingStep,
        ManifestPlanningStep,
        ManifestProductBehaviorStep,
        ManifestReviewStep,
        ManifestSourceStep,
        Overlay,
    },
    directives: {
        'b-tooltip': VBTooltip,
    },
    mixins: [backendResponse],
    props: {
        manifest: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeStepIndex: 0,
            catalog: null,
            catalogError: false,
            catalogRequestId: 0,
            completedSteps: [],
            createdVersion: null,
            createdDefinitionSignature: null,
            creatingVersion: false,
            loadingCatalog: false,
            loadingSourceVersion: false,
            mappingState: createMappingState(),
            navigationCollapsed: false,
            planningState: createPlanningState(),
            productState: createProductState(),
            requirementsRequestId: 0,
            selectedSourceVersionId: null,
            sourceState: createSourceState(),
            sourceVersionError: null,
            sourceVersionRequestId: 0,
            draftBaselineSignature: null,
            draftInitializedFor: null,
            loadedSourceVersionId: null,
            loadedSourceRevision: null,
            versionDetailsById: {},
            steps: STEPS,
        }
    },
    computed: {
        activeStep() {
            return this.steps[this.activeStepIndex]
        },
        manifestContextKey() {
            return this.manifest.id && this.manifest.manifestType && this.manifest.ownerContext
                ? `${this.manifest.id}:${this.manifest.manifestType}:${this.manifest.ownerContext}`
                : null
        },
        orderedVersions() {
            const versions = Array.isArray(this.manifest.versions) ? this.manifest.versions : []
            return [...versions].sort((left, right) => right.revision - left.revision)
        },
        sourceVersionOptions() {
            return this.orderedVersions.map(version => ({
                value: version.id,
                text: `Revision ${version.revision}${
                    version.id === this.manifest.activeVersionId ? ' · Active' : ''
                }`,
            }))
        },
        defaultSourceVersionId() {
            return this.manifest.activeVersionId
                || (this.orderedVersions.length ? this.orderedVersions[0].id : null)
        },
        draftSourceLabel() {
            if (!this.loadedSourceVersionId) {
                return 'Blank configuration'
            }
            const active = this.loadedSourceVersionId === this.manifest.activeVersionId
            return `Draft based on Revision ${this.loadedSourceRevision || '—'}${
                active ? ' · Active' : ''
            }`
        },
        hasDraftChanges() {
            return this.draftBaselineSignature !== null
                && this.definitionSignature !== this.draftBaselineSignature
        },
        canLoadSelectedVersion() {
            return Boolean(
                this.selectedSourceVersionId
                && !this.disabled
                && !this.loadingSourceVersion
                && (
                    this.selectedSourceVersionId !== this.loadedSourceVersionId
                    || this.hasDraftChanges
                )
            )
        },
        canResetConfiguration() {
            return Boolean(this.loadedSourceVersionId || this.hasEditorConfiguration)
        },
        hasEditorConfiguration() {
            return Boolean(
                this.sourceState.inspection
                || this.productState.driver
                || this.mappingState.rows.length
                || this.planningState.policy
            )
        },
        isLastStep() {
            return this.activeStepIndex === this.steps.length - 1
        },
        hasInspectedSource() {
            return Boolean(
                this.sourceState.inspection
                && this.sourceState.selectedTabIndex !== null
                && this.sourceState.headerRow
                && this.sourceState.dataStartRow
            )
        },
        selectedSourceTab() {
            if (!this.sourceState.inspection) {
                return null
            }

            return this.sourceState.inspection.tabs.find(
                item => item.index === this.sourceState.selectedTabIndex
            ) || null
        },
        sourceMappingKey() {
            if (!this.sourceState.inspection || !this.selectedSourceTab) {
                return null
            }

            return [
                this.sourceState.inspection.fileReferenceId,
                this.selectedSourceTab.index,
            ].join(':')
        },
        drivers() {
            return this.catalog && Array.isArray(this.catalog.drivers)
                ? this.catalog.drivers
                : []
        },
        selectedDriver() {
            return this.drivers.find(driver => (
                driver.key === this.productState.driver
                && driver.version === this.productState.driverVersion
            )) || null
        },
        canonicalFields() {
            return this.selectedDriver && Array.isArray(this.selectedDriver.canonicalFields)
                ? this.selectedDriver.canonicalFields
                : []
        },
        mappingComponents() {
            if (!this.catalog || !Array.isArray(this.catalog.components)) {
                return []
            }

            return this.catalog.components.filter(component => [
                'value_extractor',
                'normalizer',
                'validator',
                'condition',
                'transformer',
            ].includes(component.kind))
        },
        defaultSourceExtractor() {
            const extractors = this.mappingComponents.filter(component => (
                component.kind === 'value_extractor'
                && component.status !== 'deprecated'
                && [
                    'system.import.extractor.column_by_header',
                    'system.import.extractor.first_present_column',
                ].includes(component.key)
            ))

            return extractors.find(component => (
                component.key === 'system.import.extractor.column_by_header'
            )) || extractors[0] || null
        },
        recordPipelineComponents() {
            if (!this.catalog || !Array.isArray(this.catalog.components)) {
                return []
            }

            return this.catalog.components.filter(component => component.kind === 'record_transformer')
        },
        planningPolicies() {
            return this.selectedDriver && Array.isArray(this.selectedDriver.planningPolicies)
                ? this.selectedDriver.planningPolicies
                : []
        },
        selectedPlanningPolicy() {
            return this.planningPolicies.find(policy => (
                policy.key === this.planningState.policy
                && policy.version === this.planningState.policyVersion
            )) || null
        },
        planningConfigured() {
            return Boolean(this.selectedPlanningPolicy)
                && configurationIssues(
                    this.selectedPlanningPolicy.configurationSchema,
                    this.planningState.configuration
                ).length === 0
        },
        definitionBuild() {
            return buildManifestDefinition({
                sourceState: this.sourceState,
                mappingState: this.mappingState,
                productState: this.productState,
                planningState: this.planningState,
                components: this.catalog && Array.isArray(this.catalog.components)
                    ? this.catalog.components
                    : [],
            })
        },
        manifestDefinition() {
            return this.definitionBuild.definition
        },
        definitionBuildIssues() {
            return this.definitionBuild.issues
        },
        definitionSignature() {
            return JSON.stringify(this.manifestDefinition)
        },
        canCreateVersion() {
            return Boolean(this.manifest.id)
                && !this.disabled
                && !this.creatingVersion
                && this.definitionBuildIssues.length === 0
                && this.createdDefinitionSignature !== this.definitionSignature
        },
        createVersionButtonLabel() {
            if (this.creatingVersion) {
                return 'Creating version'
            }
            return this.createdDefinitionSignature === this.definitionSignature
                ? 'Version created'
                : 'Create version'
        },
        productBehaviorConfigured() {
            return Boolean(
                this.productState.driver
                && this.productState.driverVersion
                && this.productState.configuration
                && this.productState.requirementsLoaded
                && !this.productState.requirementsError
            )
        },
        mappingRequirementStatuses() {
            const mappedFields = new Set(
                this.mappingState.rows
                    .filter(row => row.targetFieldKey)
                    .map(row => row.targetFieldKey)
            )

            return this.productState.requirements.map(requirement => {
                const fields = Array.isArray(requirement.fields) ? requirement.fields : []
                const mappedCount = fields.filter(field => mappedFields.has(field)).length
                const minimum = Number(requirement.minimumMappedFields) || 0

                return {
                    ...requirement,
                    mappedCount,
                    satisfied: mappedCount >= minimum,
                }
            })
        },
        mappingRequirementsSatisfied() {
            return this.productBehaviorConfigured
                && this.mappingRequirementStatuses.every(requirement => requirement.satisfied)
        },
        requirementsRequestKey() {
            if (
                !this.productState.driver
                || !this.productState.driverVersion
                || !this.productState.configuration
            ) {
                return null
            }

            return JSON.stringify({
                driver: this.productState.driver,
                driverVersion: this.productState.driverVersion,
                configuration: this.productState.configuration,
            })
        },
        canContinue() {
            if (this.disabled) {
                return false
            }
            if (this.activeStep.id === 'source') {
                return this.hasInspectedSource
            }
            if (this.activeStep.id === 'product') {
                return this.productBehaviorConfigured
            }
            if (this.activeStep.id === 'mapping') {
                return this.mappingRequirementsSatisfied
            }
            if (this.activeStep.id === 'planning') {
                return this.planningConfigured
            }

            return true
        },
        sourceSummary() {
            if (!this.sourceState.inspection) {
                return null
            }

            const tab = this.sourceState.inspection.tabs.find(
                item => item.index === this.sourceState.selectedTabIndex
            )

            return {
                filename: this.sourceState.inspection.filename,
                tabName: tab ? tab.name : null,
                columnCount: tab ? tab.columns.length : 0,
            }
        },
    },
    watch: {
        manifestContextKey: {
            immediate: true,
            handler(contextKey) {
                this.changeManifestContext(contextKey)
            },
        },
        sourceMappingKey: {
            immediate: true,
            handler(sourceKey) {
                this.initializeMapping(sourceKey)
            },
        },
        requirementsRequestKey: {
            immediate: true,
            handler(requestKey) {
                if (requestKey) {
                    this.resolveMappingRequirements()
                } else {
                    this.clearMappingRequirements()
                }
            },
        },
        canonicalFields(fields) {
            const keys = new Set(fields.map(field => field.key))
            const rows = this.mappingState.rows.map(row => (
                row.targetFieldKey && !keys.has(row.targetFieldKey)
                    ? {...row, targetFieldKey: null, pipeline: []}
                    : row
            ))

            this.mappingState = {...this.mappingState, rows}
        },
        planningPolicies(policies) {
            const selectedPolicyIsAvailable = policies.some(policy => (
                policy.key === this.planningState.policy
                && policy.version === this.planningState.policyVersion
            ))
            if (!selectedPolicyIsAvailable) {
                this.planningState = createPlanningState()
            }
        },
        definitionSignature(value, previousValue) {
            if (previousValue && value !== previousValue) {
                this.createdVersion = null
            }
        },
    },
    methods: {
        changeManifestContext(contextKey) {
            this.catalogRequestId += 1
            this.requirementsRequestId += 1
            this.sourceVersionRequestId += 1
            this.catalog = null
            this.catalogError = false
            this.createdVersion = null
            this.createdDefinitionSignature = null
            this.draftBaselineSignature = null
            this.draftInitializedFor = null
            this.loadedSourceVersionId = null
            this.loadedSourceRevision = null
            this.loadingSourceVersion = false
            this.selectedSourceVersionId = null
            this.sourceVersionError = null
            this.versionDetailsById = {}
            this.resetEditorState()

            if (contextKey) {
                this.loadCatalog()
            }
        },
        loadCatalog() {
            const requestId = ++this.catalogRequestId
            this.loadingCatalog = true
            this.catalogError = false

            ImportManifestApi
                .getCatalog(this.manifest.manifestType, this.manifest.ownerContext)
                .then(response => {
                    if (requestId !== this.catalogRequestId) {
                        return
                    }
                    this.catalog = response.data
                    this.initializeDraftSource()
                })
                .catch(error => {
                    if (requestId !== this.catalogRequestId) {
                        return
                    }
                    this.catalogError = true
                    this.handleBackendError(error)
                })
                .finally(() => {
                    if (requestId === this.catalogRequestId) {
                        this.loadingCatalog = false
                    }
                })
        },
        initializeMapping(sourceKey) {
            if (!sourceKey || !this.selectedSourceTab) {
                return
            }
            if (this.mappingState.sourceKey === sourceKey) {
                return
            }

            const rows = this.selectedSourceTab.columns.map(column => ({
                id: `source-${column.index}`,
                sourceColumnIndex: column.index,
                sourceColumnIndexes: [column.index],
                sourceHeader: column.candidateHeader || null,
                extractorKey: this.defaultSourceExtractor
                    ? this.defaultSourceExtractor.key
                    : 'system.import.extractor.column_by_header',
                extractorVersion: this.defaultSourceExtractor
                    ? this.defaultSourceExtractor.version
                    : 1,
                targetFieldKey: null,
                pipeline: [],
                additional: false,
            }))

            this.mappingState = {
                sourceKey,
                rows,
                selectedRowId: rows.length ? rows[0].id : null,
                recordPipeline: [],
                tabExtras: {},
            }
        },
        initializeDraftSource() {
            if (this.draftInitializedFor === this.manifestContextKey) {
                return
            }

            this.draftInitializedFor = this.manifestContextKey
            this.selectedSourceVersionId = this.defaultSourceVersionId
            if (this.selectedSourceVersionId) {
                this.loadVersionAsDraft(this.selectedSourceVersionId, false)
            } else {
                this.markDraftBaseline()
            }
        },
        loadSelectedVersion() {
            if (!this.canLoadSelectedVersion) {
                return
            }
            this.loadVersionAsDraft(this.selectedSourceVersionId, this.hasDraftChanges)
        },
        async loadVersionAsDraft(versionId, confirmReplacement) {
            if (confirmReplacement && !await this.confirmDraftReplacement(
                'Loading another revision replaces the current editor configuration.'
            )) {
                return
            }

            const summary = this.orderedVersions.find(version => version.id === versionId) || {}
            const cached = this.versionDetailsById[versionId]
            const embedded = summary.definition ? summary : null
            if (cached || embedded) {
                this.applyVersionDefinition(cached || embedded)
                return
            }

            const requestId = ++this.sourceVersionRequestId
            this.loadingSourceVersion = true
            this.sourceVersionError = null
            ImportManifestApi.getVersion(this.manifest.id, versionId)
                .then(response => {
                    if (requestId !== this.sourceVersionRequestId) {
                        return
                    }
                    this.$set(this.versionDetailsById, versionId, response.data)
                    this.applyVersionDefinition(response.data)
                })
                .catch(error => {
                    if (requestId !== this.sourceVersionRequestId) {
                        return
                    }
                    this.sourceVersionError = 'The selected revision could not be loaded.'
                    this.handleBackendError(error)
                })
                .finally(() => {
                    if (requestId === this.sourceVersionRequestId) {
                        this.loadingSourceVersion = false
                    }
                })
        },
        applyVersionDefinition(version) {
            try {
                const state = hydrateManifestDefinition(version.definition, version)
                this.sourceState = state.sourceState
                this.mappingState = state.mappingState
                this.productState = state.productState
                this.planningState = state.planningState
                this.loadedSourceVersionId = version.id
                this.loadedSourceRevision = version.revision
                this.selectedSourceVersionId = version.id
                this.sourceVersionError = null
                this.createdVersion = null
                this.createdDefinitionSignature = null
                this.completedSteps = [0, 1, 2, 3]
                this.activeStepIndex = 0
                this.markDraftBaseline()
            } catch (error) {
                this.sourceVersionError = error.message
            }
        },
        async resetConfiguration() {
            if (!this.canResetConfiguration || !await this.confirmDraftReplacement(
                'Resetting removes all source, behavior, mapping, pipeline, and planning settings from this draft.'
            )) {
                return
            }

            this.sourceVersionRequestId += 1
            this.loadingSourceVersion = false
            this.resetEditorState()
            this.loadedSourceVersionId = null
            this.loadedSourceRevision = null
            this.sourceVersionError = null
            this.createdVersion = null
            this.createdDefinitionSignature = null
            this.markDraftBaseline()
        },
        resetEditorState() {
            this.activeStepIndex = 0
            this.completedSteps = []
            this.sourceState = createSourceState()
            this.mappingState = createMappingState()
            this.productState = createProductState()
            this.planningState = createPlanningState()
        },
        markDraftBaseline() {
            this.$nextTick(() => {
                this.draftBaselineSignature = this.definitionSignature
            })
        },
        confirmDraftReplacement(message) {
            if (!this.$bvModal || typeof this.$bvModal.msgBoxConfirm !== 'function') {
                return Promise.resolve(true)
            }
            return this.$bvModal.msgBoxConfirm(message, {
                title: 'Replace draft configuration?',
                okTitle: 'Replace configuration',
                cancelTitle: 'Cancel',
                okVariant: 'danger',
                cancelVariant: 'outline-secondary',
                centered: true,
            })
        },
        resolveMappingRequirements() {
            if (!this.requirementsRequestKey) {
                this.clearMappingRequirements()
                return
            }

            const requestId = ++this.requirementsRequestId
            this.productState = {
                ...this.productState,
                requirements: [],
                requirementsLoaded: false,
                requirementsError: false,
                loadingRequirements: true,
            }

            ImportManifestApi.resolveMappingRequirements(this.manifest.manifestType, {
                ownerContext: this.manifest.ownerContext,
                driver: this.productState.driver,
                driverVersion: this.productState.driverVersion,
                configuration: this.productState.configuration,
            })
                .then(response => {
                    if (requestId !== this.requirementsRequestId) {
                        return
                    }

                    this.productState = {
                        ...this.productState,
                        requirements: Array.isArray(response.data.requirements)
                            ? response.data.requirements
                            : [],
                        requirementsLoaded: true,
                        requirementsError: false,
                        loadingRequirements: false,
                    }
                })
                .catch(error => {
                    if (requestId !== this.requirementsRequestId) {
                        return
                    }

                    this.productState = {
                        ...this.productState,
                        requirements: [],
                        requirementsLoaded: false,
                        requirementsError: true,
                        loadingRequirements: false,
                    }
                    this.handleBackendError(error)
                })
        },
        clearMappingRequirements() {
            this.requirementsRequestId += 1
            if (
                !this.productState.loadingRequirements
                && !this.productState.requirementsLoaded
                && !this.productState.requirementsError
                && this.productState.requirements.length === 0
            ) {
                return
            }

            this.productState = {
                ...this.productState,
                requirements: [],
                requirementsLoaded: false,
                requirementsError: false,
                loadingRequirements: false,
            }
        },
        stepTooltip(step) {
            return `${step.title} — ${step.navigationDescription}`
        },
        canOpenStep(index) {
            return index <= this.highestAvailableStepIndex()
        },
        highestAvailableStepIndex() {
            if (!this.hasInspectedSource) {
                return 0
            }
            if (!this.productBehaviorConfigured) {
                return 1
            }
            if (!this.mappingRequirementsSatisfied) {
                return 2
            }
            if (!this.planningConfigured) {
                return 3
            }

            const completedSteps = this.completedSteps.includes(0)
                ? this.completedSteps
                : [0, ...this.completedSteps]

            return Math.min(
                Math.max(...completedSteps) + 1,
                this.steps.length - 1
            )
        },
        isStepCompleted(index) {
            if (index === 0) {
                return this.hasInspectedSource
            }
            if (index === 1) {
                return this.productBehaviorConfigured
            }
            if (index === 2) {
                return this.mappingRequirementsSatisfied
            }
            if (index === 3) {
                return this.planningConfigured
            }

            return this.completedSteps.includes(index)
        },
        openStep(index) {
            if (this.canOpenStep(index)) {
                this.activeStepIndex = index
            }
        },
        openStepById(stepId) {
            const index = this.steps.findIndex(step => step.id === stepId)
            if (index !== -1) {
                this.activeStepIndex = index
            }
        },
        createVersion() {
            if (!this.canCreateVersion) {
                return
            }

            this.creatingVersion = true
            this.createdVersion = null
            ImportManifestApi.createVersion(this.manifest.id, this.manifestDefinition)
                .then(response => {
                    this.createdVersion = response.data
                    this.createdDefinitionSignature = this.definitionSignature
                    const version = {
                        ...response.data,
                        definition: response.data.definition || this.manifestDefinition,
                    }
                    this.$set(this.versionDetailsById, version.id, version)
                    this.loadedSourceVersionId = version.id
                    this.loadedSourceRevision = version.revision
                    this.selectedSourceVersionId = version.id
                    this.draftBaselineSignature = this.definitionSignature
                    this.$emit('version-created', response.data)
                    this.showDefaultSuccessMessage(
                        `Manifest revision ${response.data.revision} has been created.`
                    )
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.creatingVersion = false
                })
        },
        nextStep() {
            if (!this.canContinue || this.isLastStep) {
                return
            }

            if (!this.completedSteps.includes(this.activeStepIndex)) {
                this.completedSteps.push(this.activeStepIndex)
            }
            this.activeStepIndex += 1
        },
        previousStep() {
            if (this.activeStepIndex > 0) {
                this.activeStepIndex -= 1
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.manifest-editor {
    display: flex;
    min-height: 34rem;
    border-top: 1px solid #ebe9f1;
}

.manifest-editor__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.manifest-editor__draft-controls {
    display: flex;
    align-items: center;
    gap: .5rem;
}

.manifest-editor__draft-controls select {
    min-width: 14rem;
}

.manifest-editor__navigation {
    flex: 0 0 17rem;
    padding: 1.5rem 1rem;
    border-right: 1px solid #ebe9f1;
    transition: flex-basis .2s ease, padding .2s ease;
}

.manifest-editor__navigation--collapsed {
    flex-basis: 4.75rem;
    padding-right: .5rem;
    padding-left: .5rem;
}

.manifest-editor__navigation-header {
    display: flex;
    min-height: 2rem;
    align-items: center;
    justify-content: space-between;
    margin-bottom: .75rem;
    padding: 0 .35rem;
}

.manifest-editor__navigation-title {
    overflow: hidden;
    font-size: .85rem;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    white-space: nowrap;
}

.manifest-editor__navigation--collapsed .manifest-editor__navigation-header {
    justify-content: center;
    padding: 0;
}

.manifest-editor__navigation--collapsed .manifest-editor__navigation-title {
    display: none;
}

.manifest-editor__step {
    display: flex;
    width: 100%;
    padding: .85rem;
    border: 0;
    border-radius: .357rem;
    color: inherit;
    background: transparent;
    text-align: left;
    transition: background-color .15s ease, color .15s ease;
}

.manifest-editor__step + .manifest-editor__step {
    margin-top: .5rem;
}

.manifest-editor__step:not(.manifest-editor__step--disabled):hover {
    background: rgba(0, 0, 0, .035);
}

.manifest-editor__step--disabled {
    color: var(--manifest-secondary-text-color, #45434c);
    cursor: not-allowed;
}

.manifest-editor__step--active {
    color: var(--primary);
    background: rgba(0, 0, 0, .045);
}

.manifest-editor__step-marker {
    display: flex;
    flex: 0 0 2rem;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    margin-right: .85rem;
    border: 1px solid #d8d6de;
    border-radius: 50%;
    font-weight: 600;
}

.manifest-editor__step--active .manifest-editor__step-marker,
.manifest-editor__step--completed .manifest-editor__step-marker {
    border-color: var(--primary);
    color: #fff;
    background: var(--primary);
}

.manifest-editor__step-copy,
.manifest-editor__step-title,
.manifest-editor__step-description {
    display: block;
}

.manifest-editor__step-copy {
    min-width: 0;
    opacity: 1;
    transition: opacity .12s ease;
}

.manifest-editor__navigation--collapsed .manifest-editor__step {
    justify-content: center;
    padding-right: .5rem;
    padding-left: .5rem;
}

.manifest-editor__navigation--collapsed .manifest-editor__step-marker {
    margin-right: 0;
}

.manifest-editor__navigation--collapsed .manifest-editor__step-copy {
    display: none;
}

.manifest-editor__step-title {
    margin-bottom: .15rem;
    font-weight: 600;
}

.manifest-editor__step-description {
    overflow: hidden;
    color: var(--manifest-secondary-text-color, #45434c);
    font-size: .85rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.manifest-editor__workspace {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    flex-direction: column;
}

.manifest-editor__workspace-header,
.manifest-editor__workspace-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem;
}

.manifest-editor__workspace-header {
    border-bottom: 1px solid #ebe9f1;
}

.manifest-editor__workspace-body {
    flex: 1 1 auto;
    padding: 1.5rem;
}

.manifest-editor__workspace-footer {
    border-top: 1px solid #ebe9f1;
}

@media (max-width: 991.98px) {
    .manifest-editor__card-header,
    .manifest-editor__draft-controls {
        align-items: stretch;
        flex-direction: column;
    }

    .manifest-editor__draft-controls select {
        min-width: 0;
    }

    .manifest-editor {
        display: block;
    }

    .manifest-editor__navigation {
        display: flex;
        overflow-x: auto;
        padding: 1rem;
        border-right: 0;
        border-bottom: 1px solid #ebe9f1;
    }

    .manifest-editor__navigation-header {
        display: none;
    }

    .manifest-editor__step {
        flex: 0 0 13rem;
    }

    .manifest-editor__navigation--collapsed .manifest-editor__step {
        justify-content: flex-start;
        padding: .85rem;
    }

    .manifest-editor__navigation--collapsed .manifest-editor__step-marker {
        margin-right: .85rem;
    }

    .manifest-editor__navigation--collapsed .manifest-editor__step-copy {
        display: block;
        opacity: 1;
    }

    .manifest-editor__step + .manifest-editor__step {
        margin-top: 0;
        margin-left: .5rem;
    }
}
</style>
